import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import FoodCard from "@/components/FoodCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DIETS = ["all", "vegetarian", "non-vegetarian", "eggitarian", "vegan"];
const MEALS = ["all", "breakfast", "lunch", "dinner", "snack"];

const TRANSLATE_LANGS = [
  { code: "english", label: "English" },
  { code: "telugu", label: "తెలుగు" },
  { code: "kannada", label: "ಕನ್ನಡ" },
  { code: "tamil", label: "தமிழ்" },
  { code: "malayalam", label: "മലയാളം" },
  { code: "hindi", label: "हिन्दी" },
];

export default function Explorer() {
  const [foods, setFoods] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("all");
  const [diet, setDiet] = useState("all");
  const [meal, setMeal] = useState("all");
  const [selected, setSelected] = useState(null);
  const [translation, setTranslation] = useState(null);  // {language, payload}
  const [translating, setTranslating] = useState(false);

  // Reset translation overlay whenever a different dish is opened + track view
  useEffect(() => {
    setTranslation(null);
    if (selected?.id) {
      api.post("/behaviour/viewed", { food_id: selected.id }).catch((e) => console.warn("[behaviour/viewed]", e?.message));
    }
  }, [selected?.id]);

  const handleTranslate = async (langCode) => {
    if (!selected || langCode === "english") {
      setTranslation(null);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post("/ai/translate", { food_id: selected.id, target_language: langCode });
      setTranslation({ language: data.language, payload: data.translation, cached: data.cached });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Translation unavailable right now.");
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    api.get("/foods/cuisines").then((r) => setCuisines(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (cuisine !== "all") params.cuisine = cuisine;
    if (diet !== "all") params.diet_type = diet;
    if (meal !== "all") params.meal_type = meal;
    api.get("/foods", { params }).then((r) => setFoods(r.data));
  }, [q, cuisine, diet, meal]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="explorer-page">
      <div className="overline mb-3">Food Universe</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight">Browse the atlas</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">A searchable catalog of dishes — filter by cuisine, diet and meal.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            placeholder="Search foods…"
            className="pl-9 h-11 rounded-full"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="explorer-search"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={cuisine} onValueChange={setCuisine}>
            <SelectTrigger className="h-11 rounded-full" data-testid="filter-cuisine"><SelectValue placeholder="Cuisine" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cuisines</SelectItem>
              {cuisines.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={diet} onValueChange={setDiet}>
            <SelectTrigger className="h-11 rounded-full" data-testid="filter-diet"><SelectValue placeholder="Diet" /></SelectTrigger>
            <SelectContent>
              {DIETS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={meal} onValueChange={setMeal}>
            <SelectTrigger className="h-11 rounded-full" data-testid="filter-meal"><SelectValue placeholder="Meal" /></SelectTrigger>
            <SelectContent>
              {MEALS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-sm text-muted-foreground mt-6" data-testid="results-count">
        {foods.length} dish{foods.length !== 1 ? "es" : ""} found
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-6">
        {foods.map((f) => <FoodCard key={f.id} food={f} onClick={() => setSelected(f)} />)}
      </div>

      {foods.length === 0 && (
        <div className="text-center py-20 text-muted-foreground" data-testid="empty-state">
          No dishes match — try widening the filters.
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <div className="overline mb-1">{selected.cuisine}{selected.district ? ` · ${selected.district}` : ""}</div>
                <DialogTitle className="font-serif text-3xl tracking-tight" data-testid="food-name">
                  {translation?.payload?.name || selected.name}
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">{selected.region}{selected.country ? `, ${selected.country}` : ""}</div>

              {/* Translation row */}
              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="translate-bar">
                <Languages className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} />
                <span className="text-xs text-muted-foreground mr-1">Read in:</span>
                {TRANSLATE_LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => handleTranslate(l.code)}
                    disabled={translating}
                    data-testid={`translate-${l.code}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition disabled:opacity-50 ${
                      (translation?.language === l.code) || (!translation && l.code === "english")
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-secondary"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
                {translating && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              </div>

              {(translation?.payload?.why_love || selected.why_love) && (
                <p className="text-sm text-foreground/90 leading-relaxed mt-3" data-testid="food-why-love">
                  {translation?.payload?.why_love || selected.why_love}
                </p>
              )}

              <div className="grid grid-cols-4 gap-4 my-5 text-center">
                <div><div className="overline">Comfort</div><div className="font-serif text-2xl font-semibold">{selected.comfort_score}</div></div>
                <div><div className="overline">Celebration</div><div className="font-serif text-2xl font-semibold">{selected.celebration_score}</div></div>
                <div><div className="overline">Energy</div><div className="font-serif text-2xl font-semibold">{selected.energy_score ?? "—"}</div></div>
                <div><div className="overline">Prep</div><div className="font-serif text-2xl font-semibold">{selected.prep_time}m</div></div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="capitalize">{selected.diet_type}</Badge>
                <Badge variant="secondary">Spice {selected.spice_level}/5</Badge>
                <Badge variant="secondary">Sweet {selected.sweetness_level}/5</Badge>
                {selected.difficulty && <Badge variant="outline">{selected.difficulty}</Badge>}
                {selected.meal_type.map((m) => <Badge key={m} variant="outline" className="capitalize">{m}</Badge>)}
              </div>

              {(translation?.payload?.taste_profile || selected.taste_profile) && (
                <div className="mt-4">
                  <div className="overline mb-1">Taste Profile</div>
                  <p className="text-sm text-foreground/85">{translation?.payload?.taste_profile || selected.taste_profile}</p>
                </div>
              )}

              {(translation?.payload?.best_time_to_eat || selected.best_time_to_eat) && (
                <div className="mt-4">
                  <div className="overline mb-1">Best Time To Eat</div>
                  <p className="text-sm text-foreground/85">{translation?.payload?.best_time_to_eat || selected.best_time_to_eat}</p>
                </div>
              )}

              {(translation?.payload?.pairings || selected.pairings) && (
                <div className="mt-4">
                  <div className="overline mb-1">Perfect Pairings</div>
                  <p className="text-sm text-foreground/85">{translation?.payload?.pairings || selected.pairings}</p>
                </div>
              )}

              <div className="mt-4">
                <div className="overline mb-2">Mood Tags</div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.mood_tags.map((t) => <Badge key={t} variant="outline" className="capitalize">{t}</Badge>)}
                </div>
              </div>

              <div className="mt-4">
                <div className="overline mb-2">Weather Fit</div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.weather_fit.map((t) => <Badge key={t} variant="outline" className="capitalize">{t}</Badge>)}
                </div>
              </div>

              {selected.trigger_tags?.length > 0 && (
                <div className="mt-4">
                  <div className="overline mb-2">Triggers</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.trigger_tags.map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                  </div>
                </div>
              )}

              {selected.source_id && (
                <div className="text-[10px] text-muted-foreground mt-5 tracking-widest uppercase">YUMO Master Corpus · {selected.source_id}</div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
